import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Shield, CheckCircle, XCircle, AlertCircle, Clock,
  ChevronLeft, ChevronRight, Filter, RefreshCw, Eye, Download
} from 'lucide-react';
import { zatcaAPI } from '../../services/api';
import toast from 'react-hot-toast';

const ZatcaInvoiceLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadLogs();
  }, [page, filterStatus, filterType]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;

      const response = await zatcaAPI.getLogs(params);
      const data = response.data?.data || {};
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to load ZATCA logs:', err);
      toast.error('فشل في تحميل سجل الفواتير');
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    const map = {
      SUCCESS: { label: 'ناجح', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      WARNING: { label: 'تحذير', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
      ERROR: { label: 'فشل', color: 'bg-red-100 text-red-700', icon: XCircle },
      REJECTED: { label: 'مرفوض', color: 'bg-red-100 text-red-700', icon: XCircle },
      ALREADY_SUBMITTED: { label: 'مرسل سابقاً', color: 'bg-blue-100 text-blue-700', icon: Clock }
    };
    const s = map[status] || { label: status, color: 'bg-gray-100 text-gray-600', icon: AlertCircle };
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${s.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {s.label}
      </span>
    );
  };

  const getTypeLabel = (type) => {
    const labels = {
      'standard-compliant': 'فاتورة ضريبية',
      'standard-credit-note-compliant': 'إشعار دائن ضريبي',
      'standard-debit-note-compliant': 'إشعار مدين ضريبي',
      'simplified-compliant': 'فاتورة مبسطة',
      'simplified-credit-note-compliant': 'إشعار دائن مبسط',
      'simplified-debit-note-compliant': 'إشعار مدين مبسط',
      'real-invoice': 'فاتورة فعلية',
      'compliance-check': 'فحص امتثال'
    };
    return labels[type] || type;
  };

  const getSourceLabel = (source) => {
    const labels = {
      'compliance-check': 'فحص امتثال',
      'production': 'إنتاج',
      'manual': 'يدوي'
    };
    return labels[source] || source;
  };

  const exportToCSV = () => {
    if (!logs.length) return;

    const headers = ['التاريخ', 'الفرع', 'نوع الفاتورة', 'المبلغ', 'الضريبة', 'الإجمالي', 'الحالة', 'UUID', 'المصدر', 'البيئة', 'الخطأ'];
    const rows = logs.map(log => [
      new Date(log.submittedAt).toLocaleString('ar-SA-u-ca-gregory'),
      log.branchName || '-',
      getTypeLabel(log.invoiceType),
      log.amount ? Number(log.amount).toFixed(2) : '-',
      log.vatAmount ? Number(log.vatAmount).toFixed(2) : '-',
      log.totalAmount ? Number(log.totalAmount).toFixed(2) : '-',
      log.status,
      log.uuid || '-',
      getSourceLabel(log.source),
      log.environment === 'simulation' ? 'تجريبية' : 'إنتاج',
      log.errorMessage || '-'
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `zatca-invoice-log-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('تم تصدير السجل');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-purple-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">سجل فواتير ZATCA</h1>
              <p className="text-slate-500 text-sm mt-1">جميع الفواتير والمستندات المرسلة لهيئة الزكاة والضريبة</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadLogs}
              disabled={loading}
              className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              تحديث
            </button>
            <button
              onClick={exportToCSV}
              disabled={!logs.length}
              className="px-4 py-2 bg-green-600 text-white rounded-xl font-medium flex items-center gap-2 hover:bg-green-700 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              تصدير CSV
            </button>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'إجمالي الفواتير', value: total, color: 'bg-blue-100 text-blue-700' },
            { label: 'ناجح', value: logs.filter(l => l.status === 'SUCCESS').length, color: 'bg-green-100 text-green-700' },
            { label: 'فشل', value: logs.filter(l => l.status === 'ERROR' || l.status === 'REJECTED').length, color: 'bg-red-100 text-red-700' },
            { label: 'مرسل سابقاً', value: logs.filter(l => l.status === 'ALREADY_SUBMITTED').length, color: 'bg-amber-100 text-amber-700' },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm"
            >
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className={`text-2xl font-bold mt-1 ${stat.color.split(' ')[1]}`}>{stat.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">تصفية:</span>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
            >
              <option value="">كل الحالات</option>
              <option value="SUCCESS">ناجح</option>
              <option value="ERROR">فشل</option>
              <option value="REJECTED">مرفوض</option>
              <option value="ALREADY_SUBMITTED">مرسل سابقاً</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
            >
              <option value="">كل الأنواع</option>
              <option value="standard-compliant">فاتورة ضريبية</option>
              <option value="standard-credit-note-compliant">إشعار دائن ضريبي</option>
              <option value="standard-debit-note-compliant">إشعار مدين ضريبي</option>
              <option value="simplified-compliant">فاتورة مبسطة</option>
              <option value="simplified-credit-note-compliant">إشعار دائن مبسط</option>
              <option value="simplified-debit-note-compliant">إشعار مدين مبسط</option>
              <option value="real-invoice">فاتورة فعلية</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden"
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-20">
              <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">لا توجد فواتير مرسلة بعد</p>
              <p className="text-gray-400 text-sm mt-1">ستظهر هنا جميع الفواتير المرسلة لهيئة الزكاة</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">التاريخ والوقت</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">الفرع</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">نوع الفاتورة</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">المبلغ</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">الحالة</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">المصدر</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">البيئة</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">تفاصيل</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {logs.map((log, idx) => (
                    <tr key={log._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-500">{(page - 1) * 20 + idx + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div>{new Date(log.submittedAt).toLocaleDateString('ar-SA-u-ca-gregory')}</div>
                        <div className="text-xs text-gray-400" dir="ltr">
                          {new Date(log.submittedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {log.branchName ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
                            {log.branchName}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-medium text-gray-800">{log.invoiceTypeLabel || getTypeLabel(log.invoiceType)}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {log.totalAmount ? (
                          <div>
                            <span className="font-medium">{Number(log.totalAmount).toFixed(2)}</span>
                            <span className="text-xs text-gray-400 mr-1">ر.س</span>
                            {log.vatAmount > 0 && (
                              <div className="text-xs text-gray-400">ض: {Number(log.vatAmount).toFixed(2)}</div>
                            )}
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(log.status)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{getSourceLabel(log.source)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${log.environment === 'simulation' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                          {log.environment === 'simulation' ? 'تجريبية' : 'إنتاج'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setSelectedLog(selectedLog?._id === log._id ? null : log)}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4 text-gray-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                صفحة {page} من {totalPages} ({total} سجل)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Detail Modal */}
        {selectedLog && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 bg-white rounded-3xl border border-gray-100 shadow-sm p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">تفاصيل الإرسال</h3>
              <button onClick={() => setSelectedLog(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">نوع الفاتورة:</span>
                <p className="font-medium">{selectedLog.invoiceTypeLabel || getTypeLabel(selectedLog.invoiceType)}</p>
              </div>
              <div>
                <span className="text-gray-500">الفرع:</span>
                <p className="font-medium">{selectedLog.branchName || 'غير محدد'}</p>
              </div>
              <div>
                <span className="text-gray-500">الحالة:</span>
                <p>{getStatusBadge(selectedLog.status)}</p>
              </div>
              {selectedLog.totalAmount > 0 && (
                <div>
                  <span className="text-gray-500">المبلغ:</span>
                  <p className="font-medium">{Number(selectedLog.totalAmount).toFixed(2)} ر.س (ض: {Number(selectedLog.vatAmount || 0).toFixed(2)})</p>
                </div>
              )}
              <div>
                <span className="text-gray-500">UUID:</span>
                <p className="font-mono text-xs" dir="ltr">{selectedLog.uuid || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Invoice Hash:</span>
                <p className="font-mono text-xs break-all" dir="ltr">{selectedLog.invoiceHash || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">التاريخ:</span>
                <p dir="ltr">{new Date(selectedLog.submittedAt).toLocaleString('en-GB')}</p>
              </div>
              <div>
                <span className="text-gray-500">البيئة:</span>
                <p>{selectedLog.environment === 'simulation' ? 'تجريبية (Simulation)' : 'إنتاج (Production)'}</p>
              </div>
              {selectedLog.errorMessage && (
                <div className="md:col-span-2">
                  <span className="text-gray-500">رسالة الخطأ:</span>
                  <p className="text-red-600 bg-red-50 p-3 rounded-lg mt-1">{selectedLog.errorMessage}</p>
                </div>
              )}
              {selectedLog.responseData && (
                <div className="md:col-span-2">
                  <span className="text-gray-500">رد ZATCA:</span>
                  <pre className="bg-gray-50 p-3 rounded-lg mt-1 text-xs overflow-x-auto" dir="ltr">
                    {JSON.stringify(selectedLog.responseData, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ZatcaInvoiceLog;
